import { useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Plus, Pin, Search } from "lucide-react";
import { api, useResource, NOTE_COLORS } from "@/lib/api";
import { PageHeader, inputCls, btnPrimary, EmptyState } from "@/components/shared";
import { NoteDialog } from "@/components/notes/NoteDialog";

export const NoteCard = ({ note, onOpen, onPin }) => (
  <div data-testid={`note-card-${note.id}`} onClick={onOpen}
    className="group relative cursor-pointer rounded-2xl border border-t-line p-5 min-h-[160px] flex flex-col shadow-sm transition-[box-shadow,transform] duration-300 hover:shadow-md hover:-translate-y-0.5 fade-up"
    style={{ backgroundColor: NOTE_COLORS[note.color] || NOTE_COLORS.oat }}>
    <button data-testid={`note-pin-${note.id}`} onClick={(e) => { e.stopPropagation(); onPin?.(); }}
      className={`absolute top-3 right-3 p-1.5 rounded-lg transition-opacity ${note.pinned ? "text-t-primary opacity-100" : "text-t-faint opacity-0 group-hover:opacity-100"}`}>
      <Pin className="h-4 w-4" fill={note.pinned ? "currentColor" : "none"} />
    </button>
    <h3 className="font-serif-display text-2xl font-semibold leading-tight pr-8">{note.title}</h3>
    <p className="text-sm text-t-c4 mt-2 whitespace-pre-wrap line-clamp-5 flex-1">{note.content}</p>
    <p className="text-[11px] text-t-faint mt-3">{formatDistanceToNow(parseISO(note.updated_at), { addSuffix: true })}</p>
  </div>
);

export default function Notes() {
  const notes = useResource("/notes");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null);
  const togglePin = async (n) => notes.replace((await api.patch(`/notes/${n.id}`, { pinned: !n.pinned })).data);
  const filtered = notes.data
    .filter((n) => `${n.title} ${n.content}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned));

  return (
    <div data-testid="notes-page">
      <PageHeader eyebrow="Thoughts & reminders" title="Notes" subtitle="Recipes, wifi passwords, gift ideas, little reminders — jot it down and let it go."
        action={<button data-testid="new-note-button" className={btnPrimary} onClick={() => setDialog({})}><Plus className="h-4 w-4" /> New note</button>} />
      <div className="relative max-w-md mb-8 fade-up">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-t-faint" />
        <input data-testid="notes-search-input" className={`${inputCls} pl-11`} placeholder="Search notes..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {filtered.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((n) => <NoteCard key={n.id} note={n} onOpen={() => setDialog({ note: n })} onPin={() => togglePin(n)} />)}
        </div>
      ) : (
        !notes.loading && <EmptyState testId="notes-empty" title={q ? "No matches" : "No notes yet"} text={q ? "Try a different search." : "Start with a quick thought."} />
      )}
      <NoteDialog state={dialog} onClose={() => setDialog(null)} onSaved={notes.reload} />
    </div>
  );
}
